import React, { useEffect, useRef, useState } from 'react';
import { 
  Calendar, ExternalLink, ArrowRight, Bell, Sparkles, 
  GraduationCap, BookOpen, ClipboardList, FlaskConical, 
  CalendarDays, Clock, UserCheck, FileText, Link2,
  School, Library, Users, Briefcase
} from 'lucide-react';

const NewsEventsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const events = [
    {
      day: "12",
      month: "June",
      title: "Admission and counseling",
      description: "Admission and counseling detail login to www.aktu.ac.in",
      link: "https://www.aktu.ac.in",
      color: "sky",
      icon: GraduationCap,
      iconBg: "from-sky-500 to-sky-600"
    },
    {
      day: "12",
      month: "June",
      title: "For Counseling",
      description: "For counseling detail login to www.upsee.nic.in",
      link: "https://www.upsee.nic.in",
      color: "red",
      icon: ClipboardList,
      iconBg: "from-red-500 to-red-600"
    },
    {
      day: "12",
      month: "June",
      title: "For D.pharma admission & counseling",
      description: "For D.pharma admission & counseling detail login to www.jeecup.nic.in",
      link: "https://www.jeecup.nic.in",
      color: "sky",
      icon: FlaskConical,
      iconBg: "from-sky-500 to-sky-600"
    },
    {
      day: "22",
      month: "Oct",
      title: "D.Pharma Jeecup Admission Started",
      description: "For D.pharma Jeecup admission detail login to www.jeecup.nic.in",
      link: "https://www.jeecup.nic.in",
      color: "red",
      icon: CalendarDays,
      iconBg: "from-red-500 to-red-600"
    }
  ];

  return (
    <section ref={sectionRef} className="py-16 md:py-20 bg-gradient-to-br from-gray-50 via-white to-sky-50/30 overflow-hidden">
      <div className="container mx-auto px-4">
        <div className={`text-center mb-12 transition-all duration-1000 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">
            News & <span className="bg-gradient-to-r from-sky-600 to-red-600 bg-clip-text text-transparent">Events</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-sky-500 via-red-500 to-sky-500 mx-auto rounded-full"></div>
          <p className="text-gray-600 mt-3 text-sm md:text-base">Latest updates and important announcements</p>
        </div>
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-1000 delay-300 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          {events.map((event, idx) => {
            const IconComponent = event.icon;
            return (
              <div
                key={idx}
                className="group relative"
              >
                <div className={`absolute -inset-0.5 bg-gradient-to-r ${event.color === 'sky' ? 'from-sky-400 to-sky-600' : 'from-red-400 to-red-600'} rounded-2xl blur opacity-0 group-hover:opacity-50 transition duration-500`}></div>
                <div className="relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden h-full transform group-hover:-translate-y-2">
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${event.color === 'sky' ? 'from-sky-400 to-sky-600' : 'from-red-400 to-red-600'}`}></div>
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className={`text-center bg-gradient-to-br ${event.color === 'sky' ? 'from-sky-500 to-sky-600' : 'from-red-500 to-red-600'} rounded-xl p-3 min-w-[70px] shadow-md group-hover:scale-105 transition-transform duration-300`}>
                        <div className="text-2xl font-bold text-white">{event.day}</div>
                        <div className="text-xs text-white/90 font-medium">{event.month}</div>
                      </div>
                      <div className={`bg-gradient-to-br ${event.iconBg} p-2.5 rounded-xl shadow-md group-hover:scale-110 transition-transform duration-300`}>
                        <IconComponent className="w-5 h-5 text-white" />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <h3 className="text-base md:text-lg font-bold text-gray-800 line-clamp-2 group-hover:text-sky-600 transition-colors">
                        {event.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">
                        {event.description}
                      </p>
                      <a
                        href={event.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-1.5 text-sm font-medium mt-2 transition-all duration-300 group/link ${event.color === 'sky' ? 'text-sky-600 hover:text-sky-700' : 'text-red-600 hover:text-red-700'}`}
                      >
                        <Link2 className="w-3.5 h-3.5" />
                        <span>Visit Website</span>
                        <ExternalLink className="w-3.5 h-3.5 group-hover/link:translate-x-0.5 transition-transform" />
                      </a>
                    </div>
                  </div>
                  <div className="absolute bottom-0 right-0 opacity-5">
                    <Calendar className="w-16 h-16 text-gray-800" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className={`text-center mt-12 transition-all duration-1000 delay-500 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <button className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl font-semibold hover:shadow-xl transition-all duration-300 group">
            <span>View All Events</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default NewsEventsSection;